"use client";

import { useState, useEffect } from "react";
import { useParams } from "@/hooks";
import { AppDispatch, RootState } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyCommissions } from "@/store/modules/myCommissions/thunks";
import { useAuth } from "@/context/AuthContext";

const useMyCommissions = ({
  dateRange,
}: {
  dateRange: { start: Date; end: Date };
}) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const { currency } = useAuth();

  const { commissions, summary, loading, error } = useSelector(
    (state: RootState) => state.myCommissions,
  );

  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      const formatLocal = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      const apiParams = {
        ...params,
        search: params.search || "",
        current_page: params.current_page || 1,
        per_page: params.per_page || 10,
        filter: {
          start_date: formatLocal(dateRange.start),
          end_date: formatLocal(dateRange.end),
          ...params.filter,
        },
      };

      dispatch(fetchMyCommissions(apiParams));
    };

    fetchData();
  }, [dispatch, dateRange, params]);

  const handlePageChange = (page: number) => {
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
    updateParams({
      search: value,
      current_page: 1,
    });
  };

  return {
    commissions: commissions?.data || [],
    loading,
    error,
    serviceCommissions: summary?.service_commissions || 0,
    productCommissions: summary?.product_commissions || 0,
    subscriptionCommissions: summary?.subscription_commissions || 0,
    packageCommissions: summary?.package_commissions || 0,
    vouchersPending: summary?.vouchers_pending || 0,
    vouchersPaid: summary?.vouchers_paid || 0,
    extrasPending: summary?.extras_pending || 0,
    extrasPaid: summary?.extras_paid || 0,
    totalPending: summary?.total_pending || 0,
    totalPaid: summary?.total_paid || 0,
    currentPage: commissions?.current_page || 1,
    handlePageChange,
    totalPages: commissions?.last_page || 1,
    itemsPerPage: commissions?.per_page || 10,
    currency: currency,
    handleFilterChange,
    searchValue,
    setParams,
    updateParams,
  };
};

export default useMyCommissions;
